import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SettingsService } from '../../services/settings/settings.service';
import { BaseComponent } from '../base/base.component';
import { SelectComponent } from '../core/controls/select.component';
import { EmptyStateComponent } from '../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../core/panels/loading-panel.component';
import { HexDashboard } from '../../models/api/dashboards/hex/hex-models';
import { PanelComponent } from '../core/panels/panel.component';
import { HomeCardComponent } from '../home/home-card.component';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-dashboards-layout',
  imports: [
    PanelComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
    HomeCardComponent,
    SelectComponent,
  ],
  template: `<div class="tw-flex tw-w-full">
    <div class="tw-grow tw-px-4 sm:tw-px-8 tw-pt-4 tw-w-full">
      <app-panel>
        <ng-template #header>
          <div
            class="tw-flex tw-items-center tw-pb-4 tw-justify-between tw-w-full tw-pr-2"
          >
            <h1>{{ pageTitle }}</h1>
            @if (dashboards.length > 1) {
              <div>
                <app-select
                  [control]="dashboard"
                  [options]="dashboards"
                  optionLabel="displayLabel"
                  optionValue="projectId"
                  [filter]="false"
                  [virtualScroll]="false"
                  placeholder="Select dashboard"
                  classes="tw-min-w-[300px]"
                  [showHelpText]="false"
                />
              </div>
            }
          </div>
        </ng-template>
        <div class="tw-grow tw-w-full tw-justify-center">
          <app-home-card type="Large">
            <div class="tw-pb-8 tw-w-full tw-min-w-0">
              <app-loading-panel
                [state]="state"
                loadingStyle="Box"
                [loadingHeight]="450"
              >
                <ng-template #init>
                  <app-empty-state
                    icon="fa-light fa-chart-tree-map"
                    size="120px"
                    label="Dashboard view"
                    message="Select a dashboard to display"
                    height="400px"
                  />
                </ng-template>
                <ng-template #empty>
                  <app-empty-state
                    icon="fa-square-exclamation fa-light"
                    label="Unable to load dashboard"
                    message="Dashboard provider unavailable at this time"
                    size="120px"
                    height="400px"
                  />
                </ng-template>
                <ng-template #loaded>
                  <iframe
                    [src]="url"
                    width="100%"
                    height="1000"
                    style="border: none;"
                    allow="clipboard-write"
                    class="tw-pt-8 tw-w-full tw-min-h-[1000px]"
                  ></iframe>
                </ng-template>
              </app-loading-panel>
            </div>
          </app-home-card>
        </div>
      </app-panel>
    </div>
  </div>`,
  styles: ``,
})
export class DashboardsLayoutComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private sanitizer = inject(DomSanitizer);
  private settings = inject(SettingsService);

  url: SafeResourceUrl | null = null;

  dashboard = new FormControl<string | null>(null);

  dashboards: (HexDashboard & { displayLabel: string })[] = [];

  state: LoadingState = 'Init';

  async ngOnInit() {
    const initData = await this.api.hexDashboards.getAvailableHexDashboards();
    if (initData?.dashboards?.length) {
      this.dashboards = initData.dashboards.map((d) => ({
        ...d,
        displayLabel: d.projectName ?? d.projectId,
      }));
    }

    const value = await this.settings.get('dashboard');
    if (value && this.dashboards.length) {
      const settings = JSON.parse(value) as DashboardSettings;
      if (this.dashboards.some((d) => d.projectId === settings.projectId)) {
        this.dashboard.setValue(settings.projectId, { emitEvent: false });
      }
    }
    if (this.dashboard.value === null && this.dashboards.length) {
      const defaultProjectId =
        initData?.defaultProjectId ?? this.dashboards[0]?.projectId;
      if (
        defaultProjectId &&
        this.dashboards.some((d) => d.projectId === defaultProjectId)
      ) {
        this.dashboard.setValue(defaultProjectId, { emitEvent: false });
      }
    }

    this.dispose.subs = this.dashboard.valueChanges
      .pipe(startWith(this.dashboard.value))
      .subscribe(async (projectId) => {
        await this.loadDashboard(projectId ?? null);
        if (projectId) {
          await this.settings.set(
            'dashboard',
            JSON.stringify({ projectId } as DashboardSettings),
          );
        }
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private async loadDashboard(projectId: string | null) {
    if (!projectId) {
      this.url = null;
      this.state = 'Init';
      return;
    }
    this.state = 'Loading';
    try {
      const projectName =
        this.dashboards.find((d) => d.projectId === projectId)?.projectName ??
        undefined;
      const result = await this.api.hexDashboards.getEmbedToken(
        projectId,
        projectName,
      );
      if (result?.embedUrl) {
        this.url = this.sanitizer.bypassSecurityTrustResourceUrl(
          result.embedUrl,
        );
        this.state = 'Loaded';
      } else {
        this.url = null;
        this.state = 'Empty';
      }
    } catch {
      this.url = null;
      this.state = 'Empty';
    }
  }
}

interface DashboardSettings {
  projectId: string;
}
